import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CastCrewPage from './pages/CastCrewPage';
import ClipsPage from './pages/ClipsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import StoryEditorPage from './pages/StoryEditorPage';
import GroupPage from './pages/GroupPage';
import RequestsPage from './pages/RequestsPage';
import AuditionsPage from './pages/AuditionsPage';
import OpenAuditionsPage from './pages/OpenAuditionsPage';
import UserSearchPage from './pages/UserSearchPage';
import ContentPage from './pages/ContentPage';
import CalendarPage from './pages/CalendarPage';
import GroupSchedulePage from './pages/GroupSchedulePage';
import PWAInstallButton from './components/PWAInstallButton';
import bgImage from './utils/FilMini_Text.png';
import './App.css';

const ChatPage = React.lazy(() => import('./pages/ChatPage'));
const DirectMessagesPage = React.lazy(() => import('./pages/DirectMessagesPage'));
const NotificationManager = React.lazy(() => import('./components/NotificationManager'));

const LoadingScreen: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundImage: `url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  }}>
    
    {/* Spinner */}
    <div style={{
      width: '50px',
      height: '50px',
      border: '5px solid rgba(255,255,255,0.3)',
      borderTop: '5px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />

    {/* Keyframes */}
    <style>
      {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

const getPostLoginPath = (profileCompleted?: boolean) =>
  profileCompleted ? '/content' : '/complete-profile';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireCompletedProfile?: boolean }> = ({
  children,
  requireCompletedProfile = true,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requireCompletedProfile && !user?.profileCompleted) {
    return <Navigate to="/complete-profile" replace />;
  }
  return <>{children}</>;
};

// Public Route Component — after login, land on /content (new home)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  return !isAuthenticated ? <>{children}</> : <Navigate to={getPostLoginPath(user?.profileCompleted)} replace />;
};

const CompleteProfileRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.profileCompleted) return <Navigate to="/content" replace />;
  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/content" replace />;
  return <Navigate to={getPostLoginPath(user?.profileCompleted)} replace />;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

// Covers dynamically rendered feed, modal, and gallery media as well as JSX
// images. Existing above-the-fold assets may opt out with data-eager-media.
const MediaPerformanceDefaults: React.FC = () => {
  React.useLayoutEffect(() => {
    const optimise = (root: ParentNode) => {
      const videos = root instanceof HTMLVideoElement ? [root] : Array.from(root.querySelectorAll<HTMLVideoElement>('video'));
      root.querySelectorAll<HTMLImageElement>('img:not([data-eager-media])').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
      });
      videos.forEach((video) => {
        if (!video.autoplay) video.preload = 'metadata';
        if (!video.querySelector('track[kind="captions"]')) {
          const track = document.createElement('track');
          track.kind = 'captions'; track.src = '/captions.vtt'; track.srclang = 'en'; track.label = 'English captions';
          video.append(track);
        }
      });
    };
    optimise(document);
    const observer = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) optimise(node);
    })));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return null;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <ScrollToTop />
          <MediaPerformanceDefaults />
          <React.Suspense fallback={null}>
            <NotificationManager />
          </React.Suspense>
          <main id="main-content">
          <React.Suspense fallback={<LoadingScreen />}>
            <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/complete-profile" element={<CompleteProfileRoute><CompleteProfilePage /></CompleteProfileRoute>} />
            {/* /content is now the landing page after login */}
            <Route path="/content" element={<ContentPage />} />
            {/* /home remains the original create/manage page */}
            <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/story/new" element={<ProtectedRoute><StoryEditorPage /></ProtectedRoute>} />
            <Route path="/story/:storyId" element={<ProtectedRoute><StoryEditorPage /></ProtectedRoute>} />
            <Route path="/group/:storyId" element={<ProtectedRoute><GroupPage /></ProtectedRoute>} />
            <Route path="/cast-crew/:storyId" element={<ProtectedRoute><CastCrewPage /></ProtectedRoute>} />
            <Route path="/requests" element={<ProtectedRoute><RequestsPage /></ProtectedRoute>} />
            <Route path="/chat/:storyId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/clips/:storyId" element={<ProtectedRoute><ClipsPage /></ProtectedRoute>} />
            <Route path="/auditions/:storyId" element={<ProtectedRoute><AuditionsPage /></ProtectedRoute>} />
            <Route path="/open-auditions" element={<OpenAuditionsPage />} />
            <Route path="/user-search" element={<ProtectedRoute><UserSearchPage /></ProtectedRoute>} />
            <Route path="/direct-messages" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
            <Route path="/direct-messages/group/:groupId" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
            <Route path="/direct-messages/:otherUserId" element={<ProtectedRoute><DirectMessagesPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/schedule/:storyId" element={<ProtectedRoute><GroupSchedulePage /></ProtectedRoute>} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
            </Routes>
          </React.Suspense>
          </main>
          <PWAInstallButton />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
